import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Center, Heading, HStack, Icon, Menu, Portal, SkeletonText, Spinner, Text, VStack } from '@chakra-ui/react';
import { OnboardingHighlightTarget, ONBOARDING_HIGHLIGHT_IDS } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightTarget';
import { requestErrorMessage } from '@src/components/sco/Requests/requestErrors';
import { GroupRequest } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import React from 'react';
import { LuCheck, LuChevronsUpDown, LuPlus } from 'react-icons/lu';
const CURRENT_GOAL_QUERY = gql`
  query WorkspaceGoalSwitcherCurrent($groupRequestId: String!) {
    groupRequest(groupRequestId: $groupRequestId) {
      id
      groupRequestId
      title
      displayTitle
      generatedTitle
      groupRequestTypeKey
      groupRequestType {
        id
        key
        title
        colorHexCode
      }
    }
  }
`;
const USER_GOALS_QUERY = gql`
  query WorkspaceGoalSwitcherList($requestingUserId: String, $page: Int, $pageSize: Int, $order: [[String]]) {
    groupRequests(requestingUserId: $requestingUserId, page: $page, pageSize: $pageSize, order: $order) {
      id
      items {
        id
        groupRequestId
        title
        displayTitle
        generatedTitle
        groupRequestTypeKey
        groupRequestType {
          id
          key
          title
          colorHexCode
        }
      }
    }
  }
`;
export type WorkspaceGoalSwitcherProps = {
  groupRequestId: string;
};
function goalTitle(request?: GroupRequest | null) {
  return request?.displayTitle || request?.title || request?.generatedTitle || 'Untitled goal';
}
function TypeBadge(t0) {
  const $ = _c(5);
  const {
    request,
    size: t1
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const typeTitle = request?.groupRequestType?.title || request?.groupRequestTypeKey || "Goal";
  const typeColor = request?.groupRequestType?.colorHexCode;
  const t2 = typeColor || "colorPalette.muted";
  const t3 = typeColor ? "white" : "colorPalette.fg";
  let t4;
  if ($[0] !== size || $[1] !== t2 || $[2] !== t3 || $[3] !== typeTitle) {
    t4 = <Badge bgColor={t2} color={t3} flexShrink={0} size={size} variant="label">{typeTitle}</Badge>;
    $[0] = size;
    $[1] = t2;
    $[2] = t3;
    $[3] = typeTitle;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

/**
 * Workspace left-nav card for the open GroupRequest.
 * Opens a menu to switch goals or start a new one (replaces My Goals list + RequestListCard).
 */
export const WorkspaceGoalSwitcher: React.FC<WorkspaceGoalSwitcherProps> = t0 => {
  const $ = _c(79);
  const {
    groupRequestId
  } = t0;
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const userId = currentUser?.userId;
  let t1;
  if ($[0] !== groupRequestId) {
    t1 = {
      groupRequestId
    };
    $[0] = groupRequestId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !groupRequestId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      variables: t1,
      skip: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const currentQuery = useQuery(CURRENT_GOAL_QUERY, t3);
  const t4 = !userId;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [["createdAt", "desc"]];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== userId) {
    t6 = {
      requestingUserId: userId,
      page: 1,
      pageSize: 50,
      order: t5
    };
    $[6] = userId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = {
      skip: t4,
      fetchPolicy: "cache-and-network",
      variables: t6
    };
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const listQuery = useQuery(USER_GOALS_QUERY, t7);
  const current = currentQuery.data?.groupRequest as GroupRequest | undefined;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t8;
  let t9;
  if ($[11] !== current || $[12] !== currentQuery.error || $[13] !== currentQuery.loading || $[14] !== groupRequestId || $[15] !== listQuery.data?.groupRequests?.items || $[16] !== listQuery.loading || $[17] !== router) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const goals = (listQuery.data?.groupRequests?.items || []).filter(_temp);
      const selected = goals.find(g => g.groupRequestId === groupRequestId) || current || null;
      if (currentQuery.loading && !current) {
        let t17;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t17 = <Box bgColor="colorPalette.bg" border="1px solid" borderColor="colorPalette.border" borderRadius="md" boxShadow="xs" padding={4} width="full"><SkeletonText gap="3" lineClamp={2} skeletonHeight="2" /></Box>;
          $[32] = t17;
        } else {
          t17 = $[32];
        }
        t16 = t17;
        break bb0;
      }
      if (currentQuery.error && !current) {
        let t17;
        if ($[33] !== currentQuery.error) {
          t17 = requestErrorMessage(currentQuery.error) || "Could not load goal";
          $[33] = currentQuery.error;
          $[34] = t17;
        } else {
          t17 = $[34];
        }
        let t18;
        if ($[35] !== t17) {
          t18 = <Center padding={3} width="full"><Text color="fg.muted" fontSize="sm">{t17}</Text></Center>;
          $[35] = t17;
          $[36] = t18;
        } else {
          t18 = $[36];
        }
        t16 = t18;
        break bb0;
      }
      T4 = OnboardingHighlightTarget;
      t14 = "md";
      t15 = ONBOARDING_HIGHLIGHT_IDS.requestListCard;
      T3 = Menu.Root;
      if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {
          placement: "bottom-start",
          sameWidth: true
        };
        $[37] = t12;
      } else {
        t12 = $[37];
      }
      let t17;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {
          bgColor: "colorPalette.subtle",
          borderColor: "colorPalette.border"
        };
        $[38] = t17;
      } else {
        t17 = $[38];
      }
      let t18;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <Text as="span" color="colorPalette.fg/50" fontSize="2xs" textStyle="label">Goal:</Text>;
        $[39] = t18;
      } else {
        t18 = $[39];
      }
      const t19 = <HStack align="center" gap={1.5} minWidth={0}>{t18}<TypeBadge request={selected} size="xs" /></HStack>;
      let t20;
      if ($[40] !== selected) {
        t20 = goalTitle(selected);
        $[40] = selected;
        $[41] = t20;
      } else {
        t20 = $[41];
      }
      let t21;
      if ($[42] !== t20) {
        t21 = <Heading fontSize="md" fontWeight="semibold" lineHeight="short" whiteSpace="normal">{t20}</Heading>;
        $[42] = t20;
        $[43] = t21;
      } else {
        t21 = $[43];
      }
      let t22;
      if ($[44] !== t19 || $[45] !== t21) {
        t22 = <VStack align="stretch" flex={1} gap={2} minWidth={0} textAlign="start">{t19}{t21}</VStack>;
        $[44] = t19;
        $[45] = t21;
        $[46] = t22;
      } else {
        t22 = $[46];
      }
      let t23;
      if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = <Icon color="colorPalette.fg/50" flexShrink={0} marginTop={0.5}><LuChevronsUpDown /></Icon>;
        $[47] = t23;
      } else {
        t23 = $[47];
      }
      if ($[48] !== t22) {
        t13 = <Menu.Trigger asChild={true}><Button alignItems="flex-start" bgColor="colorPalette.bg" border="1px solid" borderColor="colorPalette.border" borderRadius="md" boxShadow="xs" height="auto" justifyContent="flex-start" padding={4} variant="outline" whiteSpace="normal" width="full" _hover={t17}><HStack align="flex-start" gap={2} width="full">{t22}{t23}</HStack></Button></Menu.Trigger>;
        $[48] = t22;
        $[49] = t13;
      } else {
        t13 = $[49];
      }
      T2 = Portal;
      T1 = Menu.Positioner;
      T0 = Menu.Content;
      t8 = "min(22rem, 60vh)";
      t9 = "14rem";
      t10 = "auto";
      t11 = listQuery.loading && goals.length === 0 ? <Center padding={4}><Spinner size="sm" /></Center> : goals.map(goal => {
        const id = goal.groupRequestId as string;
        const isSelected = id === groupRequestId;
        return <Menu.Item key={id} closeOnSelect={true} value={id} onClick={() => {
          if (!isSelected) {
            router.push(`/requests/manage/${id}`);
          }
        }}><HStack flex={1} gap={2} minWidth={0}><TypeBadge request={goal} size="xs" /><Text flex={1} fontSize="sm" fontWeight={isSelected ? "semibold" : "medium"} lineClamp={1} minWidth={0}>{goalTitle(goal)}</Text></HStack>{isSelected ? <Icon color="fg" flexShrink={0}><LuCheck /></Icon> : null}</Menu.Item>;
      });
    }
    $[11] = current;
    $[12] = currentQuery.error;
    $[13] = currentQuery.loading;
    $[14] = groupRequestId;
    $[15] = listQuery.data?.groupRequests?.items;
    $[16] = listQuery.loading;
    $[17] = router;
    $[18] = T0;
    $[19] = T1;
    $[20] = T2;
    $[21] = T3;
    $[22] = T4;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[18];
    T1 = $[19];
    T2 = $[20];
    T3 = $[21];
    T4 = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Menu.Separator />;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== router) {
    t18 = () => {
      router.push("/requests/manage");
    };
    $[51] = router;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <LuPlus />;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t18) {
    t20 = <Menu.Item value="new-goal" onClick={t18}>{t19}Start a New Goal</Menu.Item>;
    $[54] = t18;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== T0 || $[57] !== t10 || $[58] !== t11 || $[59] !== t20 || $[60] !== t8 || $[61] !== t9) {
    t21 = <T0 maxH={t8} minW={t9} overflowY={t10}>{t11}{t17}{t20}</T0>;
    $[56] = T0;
    $[57] = t10;
    $[58] = t11;
    $[59] = t20;
    $[60] = t8;
    $[61] = t9;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== T1 || $[64] !== t21) {
    t22 = <T1>{t21}</T1>;
    $[63] = T1;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== T2 || $[67] !== t22) {
    t23 = <T2>{t22}</T2>;
    $[66] = T2;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== T3 || $[70] !== t12 || $[71] !== t13 || $[72] !== t23) {
    t24 = <T3 positioning={t12}>{t13}{t23}</T3>;
    $[69] = T3;
    $[70] = t12;
    $[71] = t13;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== T4 || $[75] !== t14 || $[76] !== t15 || $[77] !== t24) {
    t25 = <T4 borderRadius={t14} id={t15}>{t24}</T4>;
    $[74] = T4;
    $[75] = t14;
    $[76] = t15;
    $[77] = t24;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  return t25;
};
export default WorkspaceGoalSwitcher;
function _temp(item) {
  return !!item?.groupRequestId;
}